import { AlertFactory } from 'alerts/Alert'
import {
  DocumentActionsAdapter,
  SuccessfulUploadDocRes,
} from 'documents/DocumentActionsAdapter'
import type { UploadDocumentRequest } from 'documents/DocumentStore'
import { action, computed, observable, makeObservable } from 'mobx'
import { BehaviorSubject, interval, NEVER } from 'rxjs'
import { switchMap, take } from 'rxjs/operators'
import { toStream } from 'utils/mobx-util'
import { every, whileActive } from 'utils/rx-util'

export type UploadBarState = 'hidden' | 'dragging' | 'uploading' | 'resolved'

export default class UploadBarViewModel {
  @observable uploadBarState: UploadBarState = 'hidden'
  @observable uploadQueue: Set<File> = new Set()
  @observable uploadQueueTotal = 0
  @observable currentlyUploadingFile: File | null = null
  @observable successUploads: SuccessfulUploadDocRes[] = []
  @observable autoDismiss = false

  private readonly activeSubject = new BehaviorSubject<boolean>(false)
  readonly active$ = this.activeSubject.asObservable()

  readonly uploadBarState$ = toStream(() => this.uploadBarState, true)
  readonly autoDismiss$ = toStream(() => this.autoDismiss, true)

  constructor(
    private newAlert: AlertFactory,
    private uploadNewDocument: DocumentActionsAdapter['uploadNewDocument'],
  ) {
    makeObservable(this)
    // While the VM is active and auto dimiss is enabled,
    // when the upload state becomes resolved, start a timer
    // that transitions to the hidden state.
    this.uploadBarState$
      .pipe(
        switchMap((state) =>
          state === 'resolved' ? interval(5000).pipe(take(1)) : NEVER,
        ),
        whileActive(every([this.active$, this.autoDismiss$])),
      )
      .subscribe(this.setHidden)
  }

  @computed
  get uploadQueueFiles() {
    return Array.from(this.uploadQueue)
  }

  @action
  setUploadBarState(barState: UploadBarState) {
    this.uploadBarState = barState
  }

  @computed
  get uploadPercent() {
    return (
      ((this.uploadQueueTotal - this.uploadQueueFiles.length) /
        this.uploadQueueTotal) *
      100
    )
  }

  @computed
  get docsPendingUpload() {
    return this.uploadQueueFiles.length - 1
  }

  @action
  activate(autoDismiss = false) {
    this.autoDismiss = autoDismiss
    this.activeSubject.next(true)
  }

  deactivate() {
    this.activeSubject.next(false)
  }

  @action
  reset() {
    this.uploadQueue.clear()
    this.uploadQueueTotal = 0
    this.currentlyUploadingFile = null
    this.successUploads = []
  }

  @action
  setCurrentlyUploadingFile(file: File) {
    this.currentlyUploadingFile = file
  }

  @action
  async uploadFiles(requests: UploadDocumentRequest[]) {
    this.reset()
    this.setUploading()

    // Prepare uploadQueue
    requests.forEach((r) => this.uploadQueue.add(r.file))
    this.uploadQueueTotal = requests.length

    for (const request of requests) {
      this.setCurrentlyUploadingFile(request.file)
      const res = await this.uploadNewDocument(request)

      if (res.success) {
        this.successUploads.push(res)
      } else {
        this.newAlert().fromError(res.error).show()
        console.error('Error Uploading Document: ', res.file.name, res.error)
      }

      this.uploadQueue.delete(request.file)
    }

    this.setResolved()
  }

  /**
   * Convenience setters and computeds
   */

  @action
  setUploading() {
    this.setUploadBarState('uploading')
  }

  @computed
  get uploading() {
    return this.uploadBarState === 'uploading'
  }

  @action
  setDragging() {
    this.setUploadBarState('dragging')
  }

  @computed
  get dragging() {
    return this.uploadBarState === 'dragging'
  }

  @action.bound
  setHidden() {
    this.setUploadBarState('hidden')
  }

  @computed
  get hidden() {
    return this.uploadBarState === 'hidden'
  }

  @action
  setResolved() {
    this.setUploadBarState('resolved')
  }

  @computed
  get resolved() {
    return this.uploadBarState === 'resolved'
  }
}
